'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { BookCard } from './book-card';
import { Icon } from './icons';
import type { Book } from '@/types/catalog';

interface BookCarouselProps {
  title: string;
  eyebrow?: string;
  books: Book[];
  /** Book ids to badge as "Nuevo". */
  newIds?: string[];
}

function carBtnStyle(enabled: boolean): React.CSSProperties {
  return {
    width: 36,
    height: 36,
    borderRadius: 999,
    border: '1px solid var(--rule-2)',
    background: enabled ? 'var(--white)' : 'transparent',
    color: enabled ? 'var(--ink)' : 'var(--stone-2)',
    cursor: enabled ? 'pointer' : 'not-allowed',
    display: 'grid',
    placeItems: 'center',
    transition: 'all 120ms ease',
  };
}

export function BookCarousel({ title, eyebrow, books, newIds = [] }: BookCarouselProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [canL, setCanL] = useState(false);
  const [canR, setCanR] = useState(true);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanL(el.scrollLeft > 4);
    setCanR(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update]);

  const scrollBy = (d: number) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: d * el.clientWidth * 0.85, behavior: 'smooth' });
  };

  if (books.length === 0) return null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          {eyebrow && <div className="eyebrow" style={{ marginBottom: 8 }}>{eyebrow}</div>}
          <h2 className="serif" style={{ margin: 0, fontSize: 24, letterSpacing: '-0.005em', fontWeight: 600 }}>{title}</h2>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button aria-label="Anterior" onClick={() => scrollBy(-1)} disabled={!canL} style={carBtnStyle(canL)}>
            <Icon.arrowL />
          </button>
          <button aria-label="Siguiente" onClick={() => scrollBy(1)} disabled={!canR} style={carBtnStyle(canR)}>
            <Icon.arrowR />
          </button>
        </div>
      </div>
      <div className="scroll-row" ref={ref} role="list">
        {books.map((b, idx) => (
          <div role="listitem" key={`${b.id}-${idx}`}>
            <BookCard book={b} isNew={newIds.includes(b.id)} />
          </div>
        ))}
      </div>
    </div>
  );
}
